import { cn } from '@mdvip/shared/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

/**
 * Alert: a contained, in-page message for states the member should notice and act on. It stays
 * until dismissed or resolved: use the Toaster for transient confirmations instead.
 *
 * Layout is a grid with an optional leading icon column. Place an `Icon` (or any element that is
 * not AlertTitle/AlertDescription) as the first child to light that column up.
 *
 * Icon selectors cover both `<i>` and `<svg>`: our `Icon` renders a Font Awesome glyph as an `<i>`,
 * while the design system's own source assumes an `<svg>`.
 *
 * There is deliberately no base `[&>i]:text-current`. Every variant colors the `<i>` itself, and a
 * base rule ties with the variant's on specificity, so source order decides. Tailwind emits
 * arbitrary values before named ones, which would let `text-current` beat the warning variant's
 * `text-[var(--melon-700)]` and flatten that icon to the body text color.
 */
const alertVariants = cva(
  'relative grid w-full items-start gap-y-0.5 rounded-lg border px-4 py-3 text-sm [&>i]:translate-y-0.5 [&>svg]:size-4 [&>svg]:translate-y-0.5',
  {
    variants: {
      variant: {
        default: 'border-border bg-card text-card-foreground [&>i]:text-foreground [&>svg]:text-foreground',
        info: 'border-info-foreground/25 bg-info text-foreground [&>i]:text-info-foreground [&>svg]:text-info-foreground',
        warning:
          'border-warning-foreground/25 bg-warning text-foreground [&>i]:text-warning-foreground [&>svg]:text-warning-foreground',
        success:
          'border-success-foreground/25 bg-success text-foreground [&>i]:text-success-foreground [&>svg]:text-success-foreground',
        destructive:
          'border-destructive-foreground/25 bg-destructive text-foreground [&>i]:text-destructive-foreground [&>svg]:text-destructive-foreground',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

/** Only a warning or a destructive alert is urgent enough to interrupt a screen reader. */
const ALERT_ROLE = {
  default: 'status',
  info: 'status',
  success: 'status',
  warning: 'alert',
  destructive: 'alert',
} as const satisfies Record<NonNullable<VariantProps<typeof alertVariants>['variant']>, 'status' | 'alert'>;

function Alert({
  className,
  variant,
  children,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  const hasIcon = React.Children.toArray(children).some(
    (child) => React.isValidElement(child) && child.type !== AlertTitle && child.type !== AlertDescription,
  );

  return (
    <div
      role={ALERT_ROLE[variant ?? 'default']}
      data-slot="alert"
      className={cn(
        alertVariants({ variant }),
        hasIcon ? 'grid-cols-[calc(var(--spacing)*4)_1fr] gap-x-3' : 'grid-cols-[0_1fr]',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn('col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight text-foreground', className)}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'col-start-2 grid justify-items-start gap-1 text-sm text-muted-foreground [&_p]:leading-relaxed',
        className,
      )}
      {...props}
    />
  );
}

export { Alert, AlertDescription, AlertTitle, alertVariants };
